import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Copy, Check, X, AlertCircle, Loader2 } from 'lucide-react';
import { createEmailAddress, suggestEmailAddress, checkEmailAvailability } from '../../services/emailAddressService';
import { getPrimaryEmailDomain } from '../../services/emailDomainService';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * Modal for creating inbound email addresses for receiving travel confirmations
 *
 * @param {Object} props
 * @param {boolean} props.open - Modal open state
 * @param {Function} props.onOpenChange - Callback for open state change
 * @param {string} [props.type] - Email type: "executive", "trip", or "general"
 * @param {string} [props.executiveId] - Executive ID (for executive type)
 * @param {string} [props.executiveName] - Executive name (for display)
 * @param {string} [props.tripId] - Trip ID (for trip type)
 * @param {string} [props.tripTitle] - Trip title (for display)
 * @param {string} [props.defaultPrefix] - Default email prefix (for general type)
 * @param {Function} [props.onSuccess] - Callback when email address is created
 */
const CreateEmailAddressModal = ({
  open,
  onOpenChange,
  type,
  executiveId,
  executiveName,
  tripId,
  tripTitle,
  defaultPrefix,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdEmail, setCreatedEmail] = useState(null);
  const [copied, setCopied] = useState(false);

  // For executive type - editable email prefix
  const [emailPrefix, setEmailPrefix] = useState('');
  const [emailDomain, setEmailDomain] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [loadingSuggestion, setLoadingSuggestion] = useState(false);

  // Fetch suggested email on mount for executive type, or set default for general type
  useEffect(() => {
    if (open && type === 'executive' && executiveId) {
      setLoadingSuggestion(true);
      suggestEmailAddress(executiveId)
        .then(result => {
          if (result.success) {
            setEmailPrefix(result.suggested_prefix);
            setEmailDomain(result.domain);
            setAvailability({ available: true });
          }
        })
        .catch(err => {
          console.error('Error fetching suggested email:', err);
        })
        .finally(() => {
          setLoadingSuggestion(false);
        });
    } else if (open && type === 'general') {
      // Set default prefix for general type
      if (defaultPrefix) {
        setEmailPrefix(defaultPrefix);
      }
      // Fetch primary domain from backend, fall back to env var or default
      getPrimaryEmailDomain()
        .then(domain => setEmailDomain(domain))
        .catch(() => setEmailDomain(process.env.REACT_APP_INBOUND_EMAIL_DOMAIN || 'veyage.io'));
    }
  }, [open, type, executiveId, defaultPrefix]);

  // Debounced availability check
  useEffect(() => {
    if ((type !== 'executive' && type !== 'general') || !emailPrefix || emailPrefix.length < 2) {
      setAvailability(null);
      return;
    }

    const timer = setTimeout(async () => {
      setIsChecking(true);
      try {
        const result = await checkEmailAvailability(emailPrefix);
        setAvailability(result);
      } catch (err) {
        console.error('Error checking availability:', err);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [emailPrefix, type]);

  const handlePrefixChange = useCallback((e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setEmailPrefix(value);
    setAvailability(null);
  }, []);

  const handleUseSuggestion = useCallback(() => {
    if (availability?.suggestion) {
      setEmailPrefix(availability.suggestion);
    }
  }, [availability]);

  const handleCreate = async () => {
    setLoading(true);
    setError('');

    try {
      const params = {
        email_type: type,
        auto_parse: true,
      };

      if (type === 'executive' && executiveId) {
        params.executive_id = executiveId;
        if (emailPrefix) {
          params.custom_email_prefix = emailPrefix;
        }
      } else if (type === 'trip' && tripId) {
        params.trip_id = tripId;
      } else if (type === 'general') {
        params.custom_email_prefix = emailPrefix;
      }

      const result = await createEmailAddress(params);

      if (result.success) {
        setCreatedEmail(result.inbound_email_address);
        if (onSuccess) {
          onSuccess(result.inbound_email_address);
        }
      } else {
        setError('Failed to create email address');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create email address');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyEmail = async () => {
    if (createdEmail?.email_address) {
      try {
        await navigator.clipboard.writeText(createdEmail.email_address);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleClose = () => {
    setCreatedEmail(null);
    setError('');
    setCopied(false);
    setEmailPrefix('');
    setEmailDomain('');
    setAvailability(null);
    onOpenChange(false);
  };

  const canCreate = type === 'trip' || (
    (type === 'executive' || type === 'general') &&
    emailPrefix.length >= 2 &&
    availability?.available &&
    !isChecking
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {!createdEmail ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-ui-violet-fg" />
                Create Email Address
              </DialogTitle>
              <DialogDescription>
                Create a dedicated email address for receiving travel confirmations.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="rounded-lg bg-ui-sunk p-4">
                <h4 className="text-sm font-semibold text-ui-ink mb-2">
                  {type === 'executive' ? 'Executive Email' : type === 'general' ? 'General Inbox' : 'Trip Email'}
                </h4>
                <p className="text-sm text-ui-ink-3 mb-3">
                  {type === 'executive' ? (
                    <>
                      Confirmations sent to this email will become <strong>unfilled components</strong> for{' '}
                      <strong>{executiveName || 'this executive'}</strong>. You can review and assign
                      them to trips later.
                    </>
                  ) : type === 'general' ? (
                    <>
                      A shared inbox for receiving travel confirmations. The system will automatically
                      match the sender's email to executives or EAs in your organization and assign
                      the reservations accordingly.
                    </>
                  ) : (
                    <>
                      Confirmations sent to this email will be automatically added to the trip{' '}
                      <strong>{tripTitle || 'this trip'}</strong>.
                    </>
                  )}
                </p>
                <div className="text-xs text-ui-muted space-y-1">
                  <p>✓ Automatic AI parsing of attachments</p>
                  <p>✓ Supports PDF, images, and documents</p>
                  <p>✓ Works with flights, hotels, car rentals, and more</p>
                  {type === 'general' && (
                    <p>✓ Matches sender email to executives automatically</p>
                  )}
                </div>
              </div>

              {/* Email Address Input for Executive and General Types */}
              {(type === 'executive' || type === 'general') && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-ui-ink-2">
                    Email Address
                  </label>
                  {loadingSuggestion ? (
                    <div className="flex items-center gap-2 text-sm text-ui-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading suggested address...
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={emailPrefix}
                          onChange={handlePrefixChange}
                          placeholder="jsmith-travel"
                          className={`flex-1 px-3 py-2 text-sm border rounded-l-md focus:outline-none focus:ring-2 focus:ring-ui-violet-solid ${
                            availability?.available === false
                              ? 'border-ui-red-line bg-ui-red-bg'
                              : availability?.available
                                ? 'border-ui-green-line bg-ui-green-bg'
                                : 'border-ui-control'
                          }`}
                        />
                        <span className="px-3 py-2 text-sm bg-ui-hover border border-l-0 border-ui-control rounded-r-md text-ui-ink-3">
                          @{emailDomain || 'veyage.io'}
                        </span>
                      </div>

                      {/* Availability Status */}
                      <div className="min-h-[20px]">
                        {isChecking && (
                          <p className="text-xs text-ui-muted flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Checking availability...
                          </p>
                        )}
                        {!isChecking && availability?.available && (
                          <p className="text-xs text-ui-green-fg flex items-center gap-1">
                            <Check className="h-3 w-3" />
                            This email address is available
                          </p>
                        )}
                        {!isChecking && availability?.available === false && (
                          <div className="space-y-1">
                            <p className="text-xs text-ui-red-fg flex items-center gap-1">
                              <X className="h-3 w-3" />
                              {availability.error}
                            </p>
                            {availability.suggestion && (
                              <p className="text-xs text-ui-ink-3">
                                Try:{' '}
                                <button
                                  type="button"
                                  onClick={handleUseSuggestion}
                                  className="text-ui-violet-fg hover:underline font-medium"
                                >
                                  {availability.suggestion}@{emailDomain}
                                </button>
                              </p>
                            )}
                          </div>
                        )}
                        {!isChecking && emailPrefix && emailPrefix.length < 2 && (
                          <p className="text-xs text-ui-amber-fg flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            Minimum 2 characters required
                          </p>
                        )}
                      </div>

                      <p className="text-xs text-ui-muted">
                        You can customize the email address. Use letters, numbers, and hyphens only.
                      </p>
                    </>
                  )}
                </div>
              )}

              {error && (
                <div className="rounded-lg bg-ui-red-bg border border-ui-red-line p-3">
                  <p className="text-sm text-ui-red-fg">{error}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCreate}
                disabled={loading || !canCreate}
                className="bg-ui-violet-strong hover:bg-ui-violet-strong/90"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create Email Address'
                )}
              </Button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-ui-green-fg">
                <Check className="h-5 w-5" />
                Email Address Created!
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="rounded-lg bg-ui-green-bg border border-ui-green-line p-4">
                <p className="text-sm text-ui-green-fg mb-3">
                  Your dedicated email address has been created. Forward travel confirmations to this
                  address and they'll be automatically processed.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-ui-ink-2">Email Address</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 rounded-lg border-2 border-ui-violet-line bg-ui-violet-bg px-4 py-3">
                    <code className="text-sm font-mono text-ui-violet-fg break-all">
                      {createdEmail.email_address}
                    </code>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCopyEmail}
                    className="flex-shrink-0"
                  >
                    {copied ? (
                      <Check className="h-4 w-4 text-ui-green-fg" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                {copied && (
                  <p className="text-xs text-ui-green-fg flex items-center gap-1">
                    <Check className="h-3 w-3" />
                    Copied to clipboard!
                  </p>
                )}
              </div>

              <div className="rounded-lg bg-ui-blue-bg border border-ui-blue-line p-4">
                <h4 className="text-sm font-semibold text-ui-blue-fg mb-2">How to use:</h4>
                <ol className="text-sm text-ui-blue-fg space-y-1 list-decimal list-inside">
                  <li>Forward travel confirmation emails to this address</li>
                  <li>Attach PDFs or images of confirmations</li>
                  <li>
                    {type === 'executive'
                      ? 'Review unfilled components and assign to trips'
                      : type === 'general'
                        ? 'System matches sender to executives automatically'
                        : 'Confirmations will appear automatically in the trip'}
                  </li>
                </ol>
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleClose}
                className="bg-ui-violet-strong hover:bg-ui-violet-strong/90"
              >
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateEmailAddressModal;
